/**
 * Конструктор шаблонов карточек фич тактики (lifecycle + секции/поля + JSON I/O).
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Crosshair, Plus, RefreshCw, Upload } from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthV2";
import { fetchCardTemplateCatalog } from "@/services/card-template-catalog.service";
import {
  tacticalFeatureCardApi,
  type TacticalFeatureCardFieldPayload,
} from "@/services/tactical-feature-card-api.service";
import { tacticalApi } from "@/services/tactical.service";
import type { TacticalFeatureCardTemplateWithDetails } from "@/types/tactical-feature-card-template";

const STATUS_LABEL: Record<string, string> = {
  draft: "Черновик",
  published: "Опубликован",
  archived: "Архив",
};

type ImportConflict = "error" | "skip" | "suffix";

export default function TacticalFeatureCardTemplateManagement() {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("tactical_feature_card_templates.manage");
  const canPublish = hasPermission("templates.publish");
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement>(null);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newFeatureKind, setNewFeatureKind] = useState<string>("");
  const [newSymbolKlsId, setNewSymbolKlsId] = useState<string>("");
  const [sectionCode, setSectionCode] = useState("main");
  const [sectionName, setSectionName] = useState("Основное");
  const [fieldKey, setFieldKey] = useState("");
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldQualId, setFieldQualId] = useState("1");
  const [rulesFieldId, setRulesFieldId] = useState<number | null>(null);
  const [visDependsOn, setVisDependsOn] = useState("");
  const [visEquals, setVisEquals] = useState("true");
  const [visEffect, setVisEffect] = useState<"hide" | "disable" | "enable">("hide");
  const [onConflict, setOnConflict] = useState<ImportConflict>("suffix");

  const catalogQuery = useQuery({
    queryKey: ["card-template-catalog", "tactical", statusFilter],
    queryFn: () =>
      fetchCardTemplateCatalog({
        module: "tactical",
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 100,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ["tactical-feature-card-template", selectedId],
    queryFn: () => tacticalFeatureCardApi.get(selectedId as number),
    enabled: selectedId != null,
  });

  const widgetsQuery = useQuery({
    queryKey: ["tactical-feature-card-widgets"],
    queryFn: () => tacticalFeatureCardApi.listFieldWidgetTypes(),
    enabled: canManage,
  });

  const symbolsQuery = useQuery({
    queryKey: ["tactical", "meta", "symbols"],
    queryFn: () => tacticalApi.listSymbols(),
    enabled: canManage,
  });
  const symbols = symbolsQuery.data ?? [];
  const symbolLabelById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of symbols) {
      m.set(s.kls_id, s.label || s.sidc || String(s.kls_id));
    }
    return m;
  }, [symbols]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["card-template-catalog", "tactical"] });
    if (selectedId != null) {
      await queryClient.invalidateQueries({ queryKey: ["tactical-feature-card-template", selectedId] });
    }
  };

  const createMut = useMutation({
    mutationFn: () =>
      tacticalFeatureCardApi.create({
        code: newCode.trim(),
        name: newName.trim(),
        feature_kind: newFeatureKind.trim() || null,
        symbol_kls_id:
          newFeatureKind === "point" && newSymbolKlsId
            ? Number(newSymbolKlsId)
            : null,
        is_default: false,
      }),
    onSuccess: async (tpl) => {
      setNewCode("");
      setNewName("");
      setNewFeatureKind("");
      setNewSymbolKlsId("");
      setSelectedId(tpl.id);
      await invalidate();
      toast({ title: "Черновик создан" });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const publishMut = useMutation({
    mutationFn: (id: number) => tacticalFeatureCardApi.publish(id),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Опубликовано" });
    },
    onError: (e: Error) => toast({ title: "Публикация", description: e.message, variant: "destructive" }),
  });

  const archiveMut = useMutation({
    mutationFn: (id: number) => tacticalFeatureCardApi.archive(id),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "В архиве" });
    },
    onError: (e: Error) => toast({ title: "Архив", description: e.message, variant: "destructive" }),
  });

  const restoreMut = useMutation({
    mutationFn: (id: number) => tacticalFeatureCardApi.restore(id, { restore_as_non_default: true }),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Восстановлено" });
    },
    onError: (e: Error) => toast({ title: "Restore", description: e.message, variant: "destructive" }),
  });

  const addSectionMut = useMutation({
    mutationFn: () =>
      tacticalFeatureCardApi.addSection(selectedId as number, {
        code: sectionCode.trim(),
        name: sectionName.trim(),
      }),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Секция добавлена" });
    },
    onError: (e: Error) => toast({ title: "Секция", description: e.message, variant: "destructive" }),
  });

  const defaultWidget = widgetsQuery.data?.[0];

  const addFieldMut = useMutation({
    mutationFn: () => {
      const widgetCode = (defaultWidget?.kls_code || "string").toLowerCase();
      const payload: TacticalFeatureCardFieldPayload = {
        field_key: fieldKey.trim(),
        label: fieldLabel.trim(),
        field_type: (widgetCode as TacticalFeatureCardFieldPayload["field_type"]) || "string",
        field_type_kls_id: defaultWidget?.kls_id ?? null,
        qual_id: Number(fieldQualId) || 1,
        section_id: detailQuery.data?.sections?.[0]?.id ?? null,
      };
      return tacticalFeatureCardApi.addField(selectedId as number, payload);
    },
    onSuccess: async () => {
      setFieldKey("");
      setFieldLabel("");
      await invalidate();
      toast({ title: "Поле добавлено" });
    },
    onError: (e: Error) => toast({ title: "Поле", description: e.message, variant: "destructive" }),
  });

  const updateFieldRulesMut = useMutation({
    mutationFn: () => {
      if (selectedId == null || rulesFieldId == null) {
        throw new Error("Не выбрано поле");
      }
      const depends = visDependsOn.trim();
      const visibility_rules = depends
        ? {
            depends_on: depends,
            equals: visEquals === "true" ? true : visEquals === "false" ? false : visEquals,
            effect: visEffect,
          }
        : {};
      return tacticalFeatureCardApi.updateField(selectedId, rulesFieldId, { visibility_rules });
    },
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Правила поля сохранены" });
    },
    onError: (e: Error) =>
      toast({ title: "Правила поля", description: e.message, variant: "destructive" }),
  });

  const importMut = useMutation({
    mutationFn: ({ text, conflict }: { text: string; conflict: ImportConflict }) => {
      const bundle = JSON.parse(text) as Record<string, unknown>;
      return tacticalFeatureCardApi.importTemplatesJson(bundle, conflict);
    },
    onSuccess: async (res) => {
      const created = res.results.filter((r) => r.outcome === "created").length;
      toast({
        title: "Импорт завершён",
        description: `Создано черновиков: ${created}, всего записей: ${res.results.length}`,
      });
      await invalidate();
    },
    onError: (e: Error) =>
      toast({ title: "Импорт JSON", description: e.message, variant: "destructive" }),
  });

  const downloadExport = useCallback(
    async (id: number, code: string) => {
      try {
        const data = await tacticalFeatureCardApi.exportTemplateJson(id);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tactical-feature-card-template-${code}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast({
          title: "Экспорт",
          description: e instanceof Error ? e.message : "Ошибка",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  const downloadBulkExport = useCallback(async () => {
    const ids = (catalogQuery.data?.items ?? []).map((r) => r.id);
    if (ids.length === 0) {
      toast({ title: "Экспорт", description: "Список пуст", variant: "destructive" });
      return;
    }
    try {
      const data = await tacticalFeatureCardApi.exportTemplatesBulk(ids.slice(0, 50));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tactical-feature-card-templates-export.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Экспорт списка",
        description: e instanceof Error ? e.message : "Ошибка",
        variant: "destructive",
      });
    }
  }, [catalogQuery.data?.items, toast]);

  const ready = catalogQuery.data?.module_ready !== false;
  const items = catalogQuery.data?.items ?? [];
  const detail: TacticalFeatureCardTemplateWithDetails | undefined = detailQuery.data;
  const isDraft = detail?.status === "draft";

  const statusBadge = useMemo(() => {
    if (!detail) return null;
    return <Badge variant="secondary">{STATUS_LABEL[detail.status] ?? detail.status}</Badge>;
  }, [detail]);

  if (!ready) {
    return (
      <p className="text-sm text-muted-foreground">
        {catalogQuery.data?.message || "Модуль шаблонов фич тактики недоступен."}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Crosshair className="h-4 w-4" />
        <h3 className="font-medium">Шаблоны фич тактики</h3>
        <SectionHelpTooltip label="Справка: шаблоны фичи">
          <p>Конструктор структуры карточки условного фичи: draft → publish → archive.</p>
          <p>
            Scope — feature_kind (point/line/polygon/text или универсальный) и опционально MIL-символ
            (только point). Runtime-значения — в properties.card_values.
          </p>
          <p>
            Условная видимость полей — visibility_rules (depends_on / equals / effect) в draft; без
            отдельных таблиц card_field_rules и без profiles.
          </p>
        </SectionHelpTooltip>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto"
          onClick={() => void catalogQuery.refetch()}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 border rounded-md p-3">
        <div className="flex items-center gap-1 mr-2">
          <span className="text-sm font-medium">Импорт / экспорт</span>
          <SectionHelpTooltip label="Справка: I/O шаблонов фичи">
            <p>Перенос структуры шаблона между контурами в JSON-пакете geopulseml.tactical_feature_card_template_export v1.</p>
            <ul className="list-disc pl-4 space-y-1 mt-1">
              <li>Экспорт — выбранный шаблон или весь текущий список (до 50).</li>
              <li>Импорт создаёт только черновики; конфликт кода: error / skip / suffix.</li>
              <li>is_default при импорте сбрасывается — default назначайте после publish.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!detail}
          onClick={() => detail && void downloadExport(detail.id, detail.code)}
        >
          <Download className="h-4 w-4 mr-1" />
          Экспорт выбранного
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => void downloadBulkExport()}>
          <Download className="h-4 w-4 mr-1" />
          Экспорт списка
        </Button>
        {canManage && (
          <>
            <div className="space-y-1">
              <Label className="text-xs">При конфликте code</Label>
              <Select value={onConflict} onValueChange={(v) => setOnConflict(v as ImportConflict)}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="error">error</SelectItem>
                  <SelectItem value="skip">skip</SelectItem>
                  <SelectItem value="suffix">suffix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                ev.target.value = "";
                if (!f) return;
                try {
                  const text = await f.text();
                  importMut.mutate({ text, conflict: onConflict });
                } catch (e) {
                  toast({
                    title: "Файл",
                    description: e instanceof Error ? e.message : "Не удалось прочитать файл",
                    variant: "destructive",
                  });
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={importMut.isPending}
              onClick={() => importInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-1" />
              Импорт JSON
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label>Статус</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все</SelectItem>
              <SelectItem value="draft">Черновик</SelectItem>
              <SelectItem value="published">Опубликован</SelectItem>
              <SelectItem value="archived">Архив</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <>
            <div className="space-y-1">
              <Label>Код</Label>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label>Имя</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-48" />
            </div>
            <div className="space-y-1">
              <Label>kind</Label>
              <Select
                value={newFeatureKind || "__universal"}
                onValueChange={(v) => {
                  const kind = v === "__universal" ? "" : v;
                  setNewFeatureKind(kind);
                  if (kind !== "point") setNewSymbolKlsId("");
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="универсальный" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="__universal">универсальный</SelectItem>
                  <SelectItem value="point">point</SelectItem>
                  <SelectItem value="line">line</SelectItem>
                  <SelectItem value="polygon">polygon</SelectItem>
                  <SelectItem value="text">text</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newFeatureKind === "point" && (
              <div className="space-y-1">
                <Label>символ</Label>
                <Select
                  value={newSymbolKlsId || "__any"}
                  onValueChange={(v) => setNewSymbolKlsId(v === "__any" ? "" : v)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="любой" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="__any">любой (fallback)</SelectItem>
                    {symbols.map((s) => (
                      <SelectItem key={s.kls_id} value={String(s.kls_id)}>
                        {s.label || s.sidc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              type="button"
              size="sm"
              disabled={!newCode.trim() || !newName.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Создать
            </Button>
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ul className="space-y-1 border rounded-md p-2 max-h-[420px] overflow-y-auto text-sm">
          {items.length === 0 ? (
            <li className="text-muted-foreground px-2 py-1">Список пуст</li>
          ) : (
            items.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`w-full text-left rounded px-2 py-1.5 hover:bg-muted ${
                    selectedId === row.id ? "bg-muted font-medium" : ""
                  }`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <span className="block truncate">{row.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {row.code} · {STATUS_LABEL[row.status] ?? row.status}
                    {row.feature_kind != null ? ` · ${row.feature_kind}` : " · универсальный"}
                    {row.symbol_kls_id != null
                      ? ` · ${symbolLabelById.get(row.symbol_kls_id) ?? `sym#${row.symbol_kls_id}`}`
                      : ""}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>

        <div className="border rounded-md p-3 space-y-3 text-sm min-h-[200px]">
          {!detail ? (
            <p className="text-muted-foreground">Выберите шаблон</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium">{detail.name}</div>
                {statusBadge}
                <span className="text-xs text-muted-foreground">v{detail.version}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                code={detail.code}; feature_kind={detail.feature_kind ?? "null"}; symbol=
                {detail.symbol_kls_id != null
                  ? symbolLabelById.get(detail.symbol_kls_id) ?? detail.symbol_kls_id
                  : "null"}
              </p>
              <div className="flex flex-wrap gap-2">
                {canPublish && detail.status === "draft" && (
                  <Button size="sm" onClick={() => publishMut.mutate(detail.id)}>
                    Publish
                  </Button>
                )}
                {canPublish && detail.status === "published" && (
                  <Button size="sm" variant="outline" onClick={() => archiveMut.mutate(detail.id)}>
                    Archive
                  </Button>
                )}
                {canPublish && detail.status === "archived" && (
                  <Button size="sm" variant="outline" onClick={() => restoreMut.mutate(detail.id)}>
                    Restore
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void downloadExport(detail.id, detail.code)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  JSON
                </Button>
              </div>

              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-1">Секции</h4>
                <ul className="space-y-0.5 mb-2">
                  {(detail.sections || []).map((s) => (
                    <li key={s.id}>
                      {s.name} <span className="text-muted-foreground">({s.code})</span>
                    </li>
                  ))}
                </ul>
                {canManage && isDraft && (
                  <div className="flex gap-1 flex-wrap">
                    <Input
                      className="w-28"
                      value={sectionCode}
                      onChange={(e) => setSectionCode(e.target.value)}
                      placeholder="code"
                    />
                    <Input
                      className="w-36"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                      placeholder="name"
                    />
                    <Button size="sm" variant="outline" onClick={() => addSectionMut.mutate()}>
                      + секция
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center gap-1 mb-1">
                  <h4 className="text-xs uppercase text-muted-foreground">Поля</h4>
                  <SectionHelpTooltip label="Справка: visibility_rules поля">
                    <p>
                      depends_on — field_key другого поля; equals — ожидаемое значение; effect —
                      hide | disable | enable (как у ОИ CardSectionGroup).
                    </p>
                    <ul className="list-disc pl-4 space-y-1 mt-1">
                      <li>Правила правятся только в draft; после publish — клон.</li>
                      <li>Пустой depends_on снимает правило (visibility_rules = {"{}"}).</li>
                    </ul>
                  </SectionHelpTooltip>
                </div>
                <ul className="space-y-1 mb-2">
                  {(detail.fields || []).map((f) => {
                    const vis = f.visibility_rules ?? {};
                    const dep =
                      typeof vis.depends_on === "string" ? vis.depends_on : "";
                    return (
                      <li key={f.id} className="rounded border px-2 py-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>
                            {f.label}{" "}
                            <span className="text-muted-foreground">({f.field_key})</span>
                          </span>
                          {dep ? (
                            <Badge variant="outline" className="text-[10px] font-normal">
                              vis: {dep}={String(vis.equals ?? true)} → {String(vis.effect ?? "enable")}
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">без vis</span>
                          )}
                          {canManage && isDraft && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                setRulesFieldId(f.id);
                                setVisDependsOn(dep);
                                setVisEquals(
                                  vis.equals === false
                                    ? "false"
                                    : vis.equals === true || vis.equals == null
                                      ? "true"
                                      : String(vis.equals),
                                );
                                setVisEffect(
                                  vis.effect === "disable" || vis.effect === "enable"
                                    ? vis.effect
                                    : "hide",
                                );
                              }}
                            >
                              правила
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {canManage && isDraft && rulesFieldId != null && (
                  <div className="mb-2 space-y-1 rounded border border-dashed p-2">
                    <p className="text-xs text-muted-foreground">
                      Visibility для поля id={rulesFieldId}
                    </p>
                    <div className="flex flex-wrap gap-1 items-end">
                      <div className="space-y-0.5">
                        <Label className="text-[10px]">depends_on</Label>
                        <Input
                          className="h-8 w-28"
                          value={visDependsOn}
                          onChange={(e) => setVisDependsOn(e.target.value)}
                          placeholder="field_key"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[10px]">equals</Label>
                        <Input
                          className="h-8 w-24"
                          value={visEquals}
                          onChange={(e) => setVisEquals(e.target.value)}
                        />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[10px]">effect</Label>
                        <Select
                          value={visEffect}
                          onValueChange={(v) =>
                            setVisEffect(v as "hide" | "disable" | "enable")
                          }
                        >
                          <SelectTrigger className="h-8 w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[250]">
                            <SelectItem value="hide">hide</SelectItem>
                            <SelectItem value="disable">disable</SelectItem>
                            <SelectItem value="enable">enable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        disabled={updateFieldRulesMut.isPending}
                        onClick={() => updateFieldRulesMut.mutate()}
                      >
                        Сохранить
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setRulesFieldId(null)}
                      >
                        Закрыть
                      </Button>
                    </div>
                  </div>
                )}
                {canManage && isDraft && (
                  <div className="flex gap-1 flex-wrap items-center">
                    <Input
                      className="w-28"
                      value={fieldKey}
                      onChange={(e) => setFieldKey(e.target.value)}
                      placeholder="field_key"
                    />
                    <Input
                      className="w-36"
                      value={fieldLabel}
                      onChange={(e) => setFieldLabel(e.target.value)}
                      placeholder="label"
                    />
                    <Input
                      className="w-20"
                      value={fieldQualId}
                      onChange={(e) => setFieldQualId(e.target.value)}
                      placeholder="qual"
                      title="qual_id"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!fieldKey.trim() || !fieldLabel.trim()}
                      onClick={() => addFieldMut.mutate()}
                    >
                      + поле
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
